import { useState, type ReactNode, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

export type ChipTone = 'neutral' | 'info' | 'success' | 'warn' | 'danger';
export type ChipSize = 'xs' | 'sm' | 'md';

export interface ChipProps {
  label: string;
  tone?: ChipTone;
  /** Custom hex or CSS var color — overrides tone when present */
  color?: string;
  selected?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
  icon?: ReactNode;
  size?: ChipSize;
  disabled?: boolean;
}

type ToneConfig = {
  color: string;
  colorSelected: string;
  bgDefault: string;
  bgHover: string;
  bgSelected: string;
  border: string;
  borderSelected: string;
  focusRing: string;
};

const TONE: Record<ChipTone, ToneConfig> = {
  neutral: {
    color:          'var(--evo-fg-3)',
    colorSelected:  'var(--evo-fg-2)',
    bgDefault:      'var(--evo-bg-2)',
    bgHover:        'var(--evo-bg-3)',
    bgSelected:     'var(--evo-bg-3)',
    border:         'var(--evo-stroke)',
    borderSelected: 'var(--evo-stroke)',
    focusRing:      'var(--evo-fg-3)',
  },
  info: {
    color:          'var(--evo-cyan)',
    colorSelected:  'var(--evo-cyan)',
    bgDefault:      'var(--evo-cyan-12)',
    bgHover:        'var(--evo-cyan-18)',
    bgSelected:     'var(--evo-cyan-18)',
    border:         'color-mix(in srgb, var(--evo-cyan) 25%, transparent)',
    borderSelected: 'color-mix(in srgb, var(--evo-cyan) 50%, transparent)',
    focusRing:      'var(--evo-cyan)',
  },
  success: {
    color:          'var(--evo-green-300)',
    colorSelected:  'var(--evo-green-300)',
    bgDefault:      'color-mix(in srgb, var(--evo-green-300) 10%, transparent)',
    bgHover:        'color-mix(in srgb, var(--evo-green-300) 18%, transparent)',
    bgSelected:     'color-mix(in srgb, var(--evo-green-300) 18%, transparent)',
    border:         'color-mix(in srgb, var(--evo-green-300) 25%, transparent)',
    borderSelected: 'color-mix(in srgb, var(--evo-green-300) 50%, transparent)',
    focusRing:      'var(--evo-green-300)',
  },
  warn: {
    color:          'var(--evo-amber)',
    colorSelected:  'var(--evo-amber)',
    bgDefault:      'color-mix(in srgb, var(--evo-amber) 10%, transparent)',
    bgHover:        'color-mix(in srgb, var(--evo-amber) 18%, transparent)',
    bgSelected:     'color-mix(in srgb, var(--evo-amber) 18%, transparent)',
    border:         'color-mix(in srgb, var(--evo-amber) 25%, transparent)',
    borderSelected: 'color-mix(in srgb, var(--evo-amber) 50%, transparent)',
    focusRing:      'var(--evo-amber)',
  },
  danger: {
    color:          'var(--evo-danger)',
    colorSelected:  'var(--evo-danger)',
    bgDefault:      'color-mix(in srgb, var(--evo-danger) 10%, transparent)',
    bgHover:        'color-mix(in srgb, var(--evo-danger) 18%, transparent)',
    bgSelected:     'color-mix(in srgb, var(--evo-danger) 18%, transparent)',
    border:         'color-mix(in srgb, var(--evo-danger) 25%, transparent)',
    borderSelected: 'color-mix(in srgb, var(--evo-danger) 50%, transparent)',
    focusRing:      'var(--evo-danger)',
  },
};

const SZ: Record<ChipSize, { h: string; fs: string; px: string; gap: string; btnSz: string }> = {
  xs: { h: '18px', fs: '10px', px: '8px',  gap: '3px', btnSz: '12px' },
  sm: { h: '20px', fs: '11px', px: '8px',  gap: '4px', btnSz: '14px' },
  md: { h: '24px', fs: '12px', px: '10px', gap: '5px', btnSz: '16px' },
};

// ── Chip ──────────────────────────────────────────────────────────────────────
// 3 modes driven by props:
//   display  — no onClick, no onRemove
//   toggle   — onClick present → role=button, keyboard, selected state
//   removable — onRemove present → shows × button

export function Chip({
  label,
  tone = 'neutral',
  color,
  selected = false,
  onClick,
  onRemove,
  icon,
  size = 'sm',
  disabled = false,
}: ChipProps) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const hasCustomColor = color != null;
  const tc = TONE[tone];
  const sz = SZ[size];
  const isClickable = !!onClick;

  const bg = hasCustomColor
    ? `color-mix(in srgb, ${color} 12%, transparent)`
    : selected ? tc.bgSelected : isClickable && hovered ? tc.bgHover : tc.bgDefault;

  const borderColor = hasCustomColor
    ? `color-mix(in srgb, ${color} 35%, transparent)`
    : selected ? tc.borderSelected : tc.border;

  const textColor = hasCustomColor ? color : (selected ? tc.colorSelected : tc.color);

  const focusRingColor = hasCustomColor ? color : tc.focusRing;
  const focusShadow = focused && isClickable
    ? `0 0 0 2px color-mix(in srgb, ${focusRingColor} 35%, transparent)`
    : 'none';

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (!isClickable || disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  }

  return (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable && !disabled ? 0 : undefined}
      aria-pressed={isClickable ? selected : undefined}
      aria-disabled={disabled ? true : undefined}
      onClick={!disabled && isClickable ? onClick : undefined}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => { if (isClickable) setHovered(true); }}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: sz.gap,
        height: sz.h,
        paddingLeft: sz.px,
        paddingRight: onRemove ? '4px' : sz.px,
        borderRadius: 'var(--evo-r-pill)',
        border: `1px solid ${borderColor}`,
        background: bg,
        color: textColor,
        fontSize: sz.fs,
        fontFamily: 'var(--evo-font)',
        fontWeight: 500,
        lineHeight: 1,
        userSelect: 'none',
        cursor: disabled ? 'not-allowed' : isClickable ? 'pointer' : 'default',
        opacity: disabled ? 0.5 : 1,
        transition: 'background 120ms linear, border-color 120ms linear, box-shadow 120ms linear',
        outline: 'none',
        boxShadow: focusShadow,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {icon != null && (
        <span style={{ display: 'inline-flex', flexShrink: 0, lineHeight: 0 }}>
          {icon}
        </span>
      )}
      <span>{label}</span>
      {onRemove != null && (
        <button
          type="button"
          aria-label={t('common.remove')}
          disabled={disabled}
          onClick={e => {
            e.stopPropagation();
            if (!disabled) onRemove();
          }}
          onMouseEnter={e => {
            e.currentTarget.style.opacity = '1';
            e.currentTarget.style.background = 'color-mix(in srgb, currentColor 15%, transparent)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.opacity = '0.7';
            e.currentTarget.style.background = 'transparent';
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: sz.btnSz,
            height: sz.btnSz,
            borderRadius: '50%',
            border: 'none',
            background: 'transparent',
            color: 'inherit',
            cursor: disabled ? 'not-allowed' : 'pointer',
            padding: 0,
            lineHeight: 0,
            opacity: 0.7,
            flexShrink: 0,
            fontFamily: 'inherit',
          }}
        >
          <svg width="8" height="8" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
}
